import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight, Linkedin, Twitter, Facebook, Youtube } from 'lucide-react';
import logo from '../../images/logo/logo-black.png';
import sslSecure from '../../images/payment/ssl-secure.svg';
import visa from '../../images/payment/visa.svg';
import mastercard from '../../images/payment/mastercard.svg';
import mastercardSecureCode from '../../images/payment/mastercard-securecode.svg';
import paypal from '../../images/payment/paypal.svg';
import discover from '../../images/payment/discover.svg';
import './ModernFooter.scss';

const ModernFooter = () => {
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { label: 'About Us', path: '/about' },
        { label: 'Services', path: '/service' },
        { label: 'Partners', path: '/partners' },
        { label: 'Contracts', path: '/contracts' },
        { label: 'Contact', path: '/contact' },
    ];

    const services = [
        { label: 'AI Automation', path: '/ai-automation' },
        { label: 'Hardware', path: '/accessories' },
        { label: 'IT Modernization', path: '/it-modernization' },
        { label: 'Cloud Solutions', path: '/digital' },
        { label: 'Cybersecurity', path: '/cybersecurity' },
    ];

    const industries = [
        { label: 'Federal Civilian', path: '/federal-civilian' },
        { label: 'Department of Defense', path: '/department' },
        { label: 'Healthcare', path: '/health-care' },
        { label: 'Commercial', path: '/commercial' },
    ];

    const handleNavClick = () => {
        window.scrollTo(0, 0);
    };

    return (
        <footer className="jk-footer">
            {/* Main Footer */}
            <div className="jk-footer__main">
                <div className="jk-footer__container">
                    <div className="jk-footer__grid">
                        {/* Company Info */}
                        <div className="jk-footer__brand">
                            <Link to="/" className="jk-footer__logo" onClick={handleNavClick}>
                                <img src={logo} alt="Jaak Tech" />
                            </Link>
                            <p className="jk-footer__description">
                                Your trusted technology partner. We simplify IT for government,
                                healthcare, and businesses to help you succeed.
                            </p>

                            {/* Contact Info */}
                            <div className="jk-footer__contact">
                                <a href="mailto:info@jaaktech.net" className="jk-footer__contact-item">
                                    <Mail size={18} />
                                    <span>info@jaaktech.net</span>
                                </a>
                                <a href="tel:+11234567890" className="jk-footer__contact-item">
                                    <Phone size={18} />
                                    <span>+1 (240) 226-6333</span>
                                </a>
                                <div className="jk-footer__contact-item">
                                    <MapPin size={18} />
                                    <span>United States</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="jk-footer__links">
                            <h4 className="jk-footer__title">Company</h4>
                            <ul className="jk-footer__list">
                                {quickLinks.map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} onClick={handleNavClick}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Services */}
                        <div className="jk-footer__links">
                            <h4 className="jk-footer__title">Services</h4>
                            <ul className="jk-footer__list">
                                {services.map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} onClick={handleNavClick}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Markets Served */}
                        <div className="jk-footer__links">
                            <h4 className="jk-footer__title">Markets Served</h4>
                            <ul className="jk-footer__list">
                                {industries.map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} onClick={handleNavClick}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Newsletter */}
                        <div className="jk-footer__newsletter">
                            <h4 className="jk-footer__title">Stay Updated</h4>
                            <p className="jk-footer__newsletter-text">
                                Subscribe to our newsletter for the latest updates and insights.
                            </p>
                            <form className="jk-footer__form" onSubmit={(e) => e.preventDefault()}>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    className="jk-footer__input"
                                />
                                <button type="submit" className="jk-footer__submit">
                                    <ArrowRight size={20} />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trust & Payment Badges */}
            <div className="jk-footer__trust">
                <div className="jk-footer__container">
                    <div className="jk-footer__trust-content">
                        <img
                            className="jk-footer__ssl"
                            src={sslSecure}
                            alt="Secure SSL Encryption"
                        />
                        <div className="jk-footer__payments">
                            <span className="jk-footer__payments-card"><img src={discover} alt="Discover" /></span>
                            <span className="jk-footer__payments-card"><img src={visa} alt="Visa" /></span>
                            <span className="jk-footer__payments-card"><img src={mastercardSecureCode} alt="Mastercard SecureCode" /></span>
                            <span className="jk-footer__payments-card"><img src={paypal} alt="PayPal" /></span>
                            <span className="jk-footer__payments-card"><img src={mastercard} alt="Mastercard" /></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Bottom */}
            <div className="jk-footer__bottom">
                <div className="jk-footer__container">
                    <div className="jk-footer__bottom-content">
                        <p className="jk-footer__copyright">
                            &copy; {currentYear} Jaak Tech LLC. All rights reserved.
                        </p>

                        <div className="jk-footer__legal">
                            <Link to="/terms-conditions" onClick={handleNavClick}>
                                Terms & Conditions
                            </Link>
                            <Link to="/privacy-policy" onClick={handleNavClick}>
                                Privacy Policy
                            </Link>
                        </div>

                        <div className="jk-footer__social">
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                                <Linkedin size={20} />
                            </a>
                            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                                <Twitter size={20} />
                            </a>
                            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <Facebook size={20} />
                            </a>
                            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                <Youtube size={20} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default ModernFooter;
